import { ConstantBackoff, LRUBuffer, Websocket, WebsocketBuilder } from "websocket-ts"
import { v4 as uuidv4 } from "uuid"
import { PromiseController } from "./promise-controller"

type Payload = Record<string, unknown>

export class WebSocketClient {
    private readonly context: Context
    private webSocket: Websocket
    private readonly sessionId: string

    constructor(url: string, sessionId: string, log = console.log, error = console.error) {
        this.context = {
            requestMap: new Map(),
            waitMessageController: new PromiseController(),
            status: new Empty(),
            log,
            error,
        }
        this.sessionId = sessionId
        this.context.status = new NotConnected(this.context)
        this.webSocket = new WebsocketBuilder(url)
            .withBuffer(new LRUBuffer(100))
            //.withBackoff(new ExponentialBackoff(100, 7))
            //.withBackoff(new LinearBackoff(0, 1000, 15000))
            .withBackoff(new ConstantBackoff(1000)) // 1000ms = 1s infinite
            .onMessage((instance, ev) => this.context.status.onMessage(ev))
            .onOpen(() => this.context.status.onOpen())
            .onError(this.onError.bind(this))
            .onClose((instance, ev) => this.context.status.onClose(ev))
            .build()
    }

    send(message: string): void {
        this.webSocket.send(message)
    }

    waitMessage(): Promise<string> {
        return this.context.waitMessageController.wait()
    }

    sendAndReceive(message: Payload): Promise<Payload> {
        const requestId = uuidv4()
        const packedMessage = { ...message, requestId, sessionId: this.sessionId }
        this.send(JSON.stringify(packedMessage))

        const controller = new PromiseController<Payload>()
        this.context.requestMap.set(requestId, controller)

        return controller.wait()
    }

    async receiveServerNotification(): Promise<Payload | undefined> {
        const data = await this.waitMessage()
        try {
            return JSON.parse(data)
        } catch {
            /*Empty because we ignore JSON parse errors*/
        }
        return undefined
    }

    private onError() {
        // onerror is always followed by a termination of connection
        // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/error_event
        this.context.error("websocket error")
    }
}

interface Status {
    onClose(ev: CloseEvent): void
    onOpen(): void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMessage(ev: MessageEvent<any>): void
}

type Context = {
    waitMessageController: PromiseController<string>
    requestMap: Map<string, PromiseController<Payload>>
    status: Status
    log: typeof console.log
    error: typeof console.error
}

class NotConnected implements Status {
    private readonly context: Context

    constructor(context: Context) {
        this.context = context
    }

    onClose(ev: CloseEvent): void {
        this.context.log("unable to connect", ev.code)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMessage(_ev: MessageEvent<any>): void {
        this.onOpen()
    }

    onOpen(): void {
        this.context.status = new Connected(this.context)
    }
}

class Connected implements Status {
    private readonly context: Context

    constructor(context: Context) {
        this.context = context
    }

    onClose(ev: CloseEvent): void {
        // https://stackoverflow.com/questions/18803971/websocket-onerror-how-to-read-error-description
        this.context.error("websocket connection closed", ev.code, ev.reason || reason(ev))
        this.rejectAllPendingBecauseWillNeverReceiveResponse(ev.reason || reason(ev))
        this.context.status = new NotConnected(this.context)
    }

    onOpen(): void {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMessage(ev: MessageEvent<any>): void {
        this.context.waitMessageController.resolve(ev.data)
        this.context.waitMessageController = new PromiseController()
        this.tryResolvePendingRequests(ev)
    }

    private tryResolvePendingRequests(ev: MessageEvent) {
        try {
            const parsedMessage = JSON.parse(ev.data)
            const { requestId, ...payload } = parsedMessage
            const controller = this.context.requestMap.get(requestId)

            if (controller) {
                this.context.requestMap.delete(requestId)
                controller.resolve(payload)
            }
        } catch {
            //Empty because we ignore JSON parse errors
        }
    }

    private rejectAllPendingBecauseWillNeverReceiveResponse(reason: string) {
        const requestMapClone = new Map(this.context.requestMap)

        requestMapClone.forEach((controller, requestId) => {
            this.context.requestMap.delete(requestId)
            controller.reject(reason)
        })
    }
}

class Empty implements Status {
    onClose(_ev: CloseEvent): void {}

    onOpen(): void {}

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onMessage(_ev: MessageEvent<any>): void {}
}

const reason = (event: CloseEvent): string => {
    // See https://www.rfc-editor.org/rfc/rfc6455#section-7.4.1
    switch (event.code) {
        case 1000:
            return "Normal closure, meaning that the purpose for which the connection was established has been fulfilled."
        case 1001:
            return 'An endpoint is "going away", such as a server going down or a browser having navigated away from a page.'
        case 1002:
            return "An endpoint is terminating the connection due to a protocol error"
        case 1003:
            return "An endpoint is terminating the connection because it has received a type of data it cannot accept (e.g., an endpoint that understands only text data MAY send this if it receives a binary message)."
        case 1004:
            return "Reserved. The specific meaning might be defined in the future."
        case 1005:
            return "No status code was actually present."
        case 1006:
            return "The connection was closed abnormally, e.g., without sending or receiving a Close control frame"
        case 1007:
            return "An endpoint is terminating the connection because it has received data within a message that was not consistent with the type of the message (e.g., non-UTF-8 [https://www.rfc-editor.org/rfc/rfc3629] data within a text message)."
        case 1008:
            return 'An endpoint is terminating the connection because it has received a message that "violates its policy". This reason is given either if there is no other suitable reason, or if there is a need to hide specific details about the policy.'
        case 1009:
            return "An endpoint is terminating the connection because it has received a message that is too big for it to process."
        case 1010:
            return `An endpoint (client) is terminating the connection because it has expected the server to negotiate one or more extension, but the server didn't return them in the response message of the WebSocket handshake.
                Specifically, the extensions that are needed are: ${event.reason}`
        case 1011:
            return "A server is terminating the connection because it encountered an unexpected condition that prevented it from fulfilling the request."
        case 1015:
            return "The connection was closed due to a failure to perform a TLS handshake (e.g., the server certificate can't be verified)."
        default:
            return "Unknown reason"
    }
}
